import { useState, type SyntheticEvent } from 'react';
import { api, ApiRequestError } from '../api.ts';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * The deadline-briefing opt-in (T-069) — off by default on every account
 * (see `db/users.ts`'s `setEmailOptIn`), so this starts unchecked and stays
 * that way until the real stored value has loaded. The checkbox is never
 * flipped ahead of the server's answer: `emailOptIn` only ever holds the
 * last value confirmed by `getPreferences`/`updatePreferences`, so a failed
 * save leaves the control exactly where it was rather than showing a change
 * that didn't actually happen.
 *
 * A collapsed `<details>`, same idiom as `SquadLibrary` — but unlike that
 * one, `getPreferences` is only fetched the first time it's actually opened,
 * not on mount. This sits on the squad desk (T-069), which every other
 * panel there only ever calls for data the desk itself needs; a manager
 * loading their squad shouldn't also fire a preferences round trip they
 * never asked to see.
 */
export function EmailPreferences() {
  const [opened, setOpened] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  function onToggle(e: SyntheticEvent<HTMLDetailsElement>) {
    if (!e.currentTarget.open || opened) return;
    setOpened(true);

    api
      .getPreferences()
      .then(({ emailOptIn: stored }) => {
        setEmailOptIn(stored);
        setLoaded(true);
      })
      .catch((err) => {
        // `loaded` stays false, so the checkbox stays disabled rather than
        // guessing at a state that failed to load.
        setError(err instanceof ApiRequestError ? err.message : 'Could not load your email preferences.');
      });
  }

  async function onCheckboxChange(checked: boolean) {
    setStatus('saving');
    setError(null);
    try {
      const result = await api.updatePreferences(checked);
      setEmailOptIn(result.emailOptIn);
      setStatus('saved');
    } catch (err) {
      // `emailOptIn` is untouched — the control still shows the real stored
      // state, not the click that failed to take.
      setStatus('error');
      setError(err instanceof ApiRequestError ? err.message : 'Could not update your email preferences.');
    }
  }

  return (
    <details className="panel panel--accent-2" onToggle={onToggle}>
      <summary>Email preferences</summary>

      <label className="checkbox-field mt-md">
        <input
          type="checkbox"
          checked={emailOptIn}
          disabled={!loaded || status === 'saving'}
          onChange={(e) => onCheckboxChange(e.target.checked)}
        />
        Deadline briefing
      </label>
      <p className="hint">
        One email before each gameweek deadline, with injury news and price changes for your
        squad — sent up to 24 hours ahead. Off by default.
      </p>

      {status === 'saving' && (
        <p className="hint" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          Saving…
        </p>
      )}

      {status === 'saved' && (
        <p className="hint" aria-live="polite">
          {emailOptIn ? "Saved — you'll get the deadline briefing." : "Saved — you won't get these emails."}
        </p>
      )}

      {error && (
        <ul className="notices notices--flush">
          <li className="error">{error}</li>
        </ul>
      )}
    </details>
  );
}
